/**
 * Tidligere notater + Værktøjer column state.
 * Per user: `clinio-journal-write-cols:<userId>`. The unscoped key is only a
 * fallback from before the choice was stored per user. `historyOpenChosen`
 * is set when the user collapses or expands Tidligere notater; until then
 * Journal still opens the column, and a step opens it when earlier notes exist.
 * `rightOpen` is the Værktøjer column for both journal writing and a course step.
 */
export const JOURNAL_WRITE_COLUMN_STORAGE_KEY = 'clinio-journal-write-cols';

export const JOURNAL_HISTORY_WIDTH_MIN = 280;
export const JOURNAL_HISTORY_WIDTH_DEFAULT = 280;
export const JOURNAL_HISTORY_WIDTH_STEP = 16;

export type JournalWriteColumnPrefs = {
  leftOpen: boolean | null;
  rightOpen: boolean | null;
  /** True after the user collapses or expands Tidligere notater. */
  historyOpenChosen: boolean;
};

export function journalWriteColumnStorageKey(userId: string): string {
  return `${JOURNAL_WRITE_COLUMN_STORAGE_KEY}:${userId}`;
}

export function journalHistoryWidthStorageKey(userId: string): string {
  return `clinio-journal-write-hist-width:${userId}`;
}

export function maxJournalHistoryWidth(
  windowWidth = typeof window === 'undefined' ? JOURNAL_HISTORY_WIDTH_MIN * 2 : window.innerWidth,
): number {
  return Math.max(JOURNAL_HISTORY_WIDTH_MIN, Math.floor(windowWidth * 0.5));
}

export function clampJournalHistoryWidth(
  width: number,
  windowWidth?: number,
): number {
  const max = maxJournalHistoryWidth(windowWidth);
  return Math.min(max, Math.max(JOURNAL_HISTORY_WIDTH_MIN, Math.round(width)));
}

export function readJournalHistoryWidth(userId: string | null | undefined): number {
  if (!userId || typeof window === 'undefined') return JOURNAL_HISTORY_WIDTH_DEFAULT;
  try {
    const raw = window.localStorage.getItem(journalHistoryWidthStorageKey(userId));
    const parsed = raw == null ? NaN : Number(raw);
    if (!Number.isFinite(parsed)) return JOURNAL_HISTORY_WIDTH_DEFAULT;
    return clampJournalHistoryWidth(parsed);
  } catch {
    return JOURNAL_HISTORY_WIDTH_DEFAULT;
  }
}

export function writeJournalHistoryWidth(
  userId: string | null | undefined,
  width: number,
): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      journalHistoryWidthStorageKey(userId),
      String(clampJournalHistoryWidth(width)),
    );
  } catch {
    /* ignore quota / private mode */
  }
}

const EMPTY: JournalWriteColumnPrefs = {
  leftOpen: null,
  rightOpen: null,
  historyOpenChosen: false,
};

function copyPrefs(prefs: JournalWriteColumnPrefs): JournalWriteColumnPrefs {
  return { ...prefs };
}

function parseJournalWriteColumnPrefs(raw: string | null): JournalWriteColumnPrefs | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<JournalWriteColumnPrefs> | null;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return {
      leftOpen: typeof parsed.leftOpen === 'boolean' ? parsed.leftOpen : null,
      rightOpen: typeof parsed.rightOpen === 'boolean' ? parsed.rightOpen : null,
      historyOpenChosen: parsed.historyOpenChosen === true,
    };
  } catch {
    return null;
  }
}

export function readJournalWriteColumnPrefs(userId?: string | null): JournalWriteColumnPrefs {
  if (typeof window === 'undefined') return copyPrefs(EMPTY);
  try {
    if (userId) {
      const scoped = parseJournalWriteColumnPrefs(
        window.localStorage.getItem(journalWriteColumnStorageKey(userId)),
      );
      if (scoped) return scoped;
    }
    return (
      parseJournalWriteColumnPrefs(window.localStorage.getItem(JOURNAL_WRITE_COLUMN_STORAGE_KEY)) ??
      copyPrefs(EMPTY)
    );
  } catch {
    return copyPrefs(EMPTY);
  }
}

export function writeJournalWriteColumnPrefs(
  userId: string | null | undefined,
  prefs: JournalWriteColumnPrefs,
): void {
  if (typeof window === 'undefined') return;
  try {
    const key = userId ? journalWriteColumnStorageKey(userId) : JOURNAL_WRITE_COLUMN_STORAGE_KEY;
    window.localStorage.setItem(key, JSON.stringify(prefs));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Saved Tidligere notater state, or null until the user collapses or expands it. */
export function rememberedHistoryOpen(prefs: JournalWriteColumnPrefs): boolean | null {
  if (!prefs.historyOpenChosen || typeof prefs.leftOpen !== 'boolean') return null;
  return prefs.leftOpen;
}

/**
 * Open Tidligere notater on first visit. Null means leave the current choice
 * alone: a remembered collapse/expand, or a source that starts closed.
 */
export function journalHistoryDefaultOpen(
  source: string | null | undefined,
  earlierNoteCount: number,
  prefs: JournalWriteColumnPrefs,
): boolean | null {
  if (rememberedHistoryOpen(prefs) !== null) return null;
  if (source === 'journal') return true;
  if (source === 'step' && earlierNoteCount > 0) return true;
  return null;
}

/** Værktøjer stays open until the user collapses the column. */
export function readToolsColumnOpen(userId?: string | null): boolean {
  return readJournalWriteColumnPrefs(userId).rightOpen ?? true;
}

/**
 * Remember Værktøjer open or closed for this user. Leaves Tidligere notater alone.
 */
export function writeToolsColumnOpen(userId: string | null | undefined, open: boolean): void {
  const current = readJournalWriteColumnPrefs(userId);
  writeJournalWriteColumnPrefs(userId, { ...current, rightOpen: open });
}

export function defaultJournalWriteColumns(
  source: string | null | undefined,
  userId?: string | null,
): {
  leftOpen: boolean;
  rightOpen: boolean;
} {
  const stored = readJournalWriteColumnPrefs(userId);
  return {
    leftOpen: rememberedHistoryOpen(stored) ?? source === 'journal',
    rightOpen: stored.rightOpen ?? true,
  };
}
